import { Routes, Route, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useTranslation } from "@/contexts/TranslationContext";
import NewMainLayout from "@/components/Layout/NewMainLayout";
import Profile from "./Profile";
import PetProfile from "./PetProfile";
import PostDetail from "./PostDetail";
import FollowersFollowing from "./FollowersFollowing";
import Feed from "./Feed";
import Settings from "./Settings";
import Explore from "./Explore";
import UserProfile from "./UserProfile";
import Map from "./Map";
import SOS from "./SOS";
import Messages from "./Messages";
import ChatView from "./ChatView";
import Events from "./Events";
import Adoption from "./Adoption";
import AdoptionRequests from "./AdoptionRequests";
import MyAdoptionRequests from "./MyAdoptionRequests";
import Pets from "./Pets";
import Notifications from "./Notifications";
import Shop from "./Shop";
import ShopView from "./ShopView";
import ShopManage from "./ShopManage";
import ConnectShop from "./ConnectShop";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { HeroButton, SecondaryButton } from "@/components/ui/button-variants";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Heart, Camera, MapPin, MessageCircle, Users, Star, TrendingUp, Plus, PawPrint, AlertCircle, Calendar, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import feedHeroBg from '@/assets/feed-hero-bg.jpg';

const HomePage = () => {
  const { user } = useAuth();
  const { profile, pets, loading, profileExists } = useProfile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [recentPosts, setRecentPosts] = useState<any[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [trendingHashtags, setTrendingHashtags] = useState<string[]>([]);
  const [loadingHashtags, setLoadingHashtags] = useState(true);
  const [feedType, setFeedType] = useState('posts');
  const [feedItems, setFeedItems] = useState<any[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(true);

  useEffect(() => {
    if (user) {
      fetchRecentPosts();
      fetchTrendingHashtags();
      fetchFeedItems(feedType);
    }
  }, [user, feedType]);

  const fetchFeedItems = async (type: string) => {
    setLoadingFeed(true);
    try {
      let data: any[] = [];
      
      switch(type) {
        case 'posts':
          const { data: postsData, error: postsError } = await supabase
            .from('posts')
            .select(`
              *,
              profiles!posts_user_id_fkey (username, display_name, avatar_url),
              pets!posts_pet_id_fkey (name, species, avatar_url)
            `)
            .eq('is_public', true)
            .order('created_at', { ascending: false })
            .limit(10);
          if (postsError) throw postsError;
          data = postsData || [];
          break;
          
        case 'sos':
          const { data: sosData, error: sosError } = await supabase
            .from('sos_reports')
            .select(`
              *,
              profiles!sos_reports_user_id_fkey (username, display_name, avatar_url)
            `)
            .eq('status', 'open')
            .order('created_at', { ascending: false })
            .limit(10);
          if (sosError) throw sosError;
          data = sosData || [];
          break;
          
        case 'events':
          const { data: eventsData, error: eventsError } = await supabase
            .from('events')
            .select(`
              *,
              profiles!events_user_id_fkey (username, display_name, avatar_url)
            `)
            .eq('event_type', 'public')
            .gte('event_date', new Date().toISOString())
            .order('event_date', { ascending: true })
            .limit(10);
          if (eventsError) throw eventsError;
          data = eventsData || [];
          break;
          
        case 'users':
          const { data: usersData, error: usersError } = await supabase
            .from('profiles')
            .select('*')
            .eq('is_private', false)
            .order('created_at', { ascending: false })
            .limit(10);
          if (usersError) throw usersError;
          data = usersData || [];
          break;
          
        case 'adoptions':
          const { data: adoptionsData, error: adoptionsError } = await supabase
            .from('adoption_posts')
            .select(`
              *,
              profiles!adoption_posts_user_id_fkey (username, display_name, avatar_url)
            `)
            .eq('status', 'published')
            .order('created_at', { ascending: false })
            .limit(10);
          if (adoptionsError) throw adoptionsError;
          data = adoptionsData || [];
          break;
      }
      
      setFeedItems(data);
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
    } finally {
      setLoadingFeed(false);
    }
  };


  const fetchRecentPosts = async () => {
    try {
      if (!profile?.following_count || profile.following_count === 0) {
        setLoadingPosts(false);
        return;
      }

      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          profiles!posts_user_id_fkey (
            username,
            display_name,
            avatar_url
          ),
          pets!posts_pet_id_fkey (
            name,
            species,
            avatar_url
          )
        `)
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentPosts(data || []);
    } catch (error) {
      console.error('Error fetching recent posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const fetchTrendingHashtags = async () => {
    try {
      // Fetch recent posts from the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data, error } = await supabase
        .from('posts')
        .select('content')
        .eq('is_public', true)
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      // Extract hashtags from post content
      const hashtagCount: Record<string, number> = {};
      const hashtagRegex = /#[\w\u00C0-\u017F]+/gi;

      data?.forEach((post) => {
        if (post.content) {
          const matches = post.content.match(hashtagRegex);
          matches?.forEach((hashtag) => {
            const normalizedTag = hashtag.toLowerCase();
            hashtagCount[normalizedTag] = (hashtagCount[normalizedTag] || 0) + 1;
          });
        }
      });

      // Sort by frequency and get top 5
      const sortedHashtags = Object.entries(hashtagCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([tag]) => tag);

      // If no hashtags found, use defaults
      if (sortedHashtags.length === 0) {
        setTrendingHashtags(["#CaniRoma", "#GattiCute", "#AdozioneMilano", "#VeterinarioConsiglio", "#PetFriendly"]);
      } else {
        setTrendingHashtags(sortedHashtags);
      }
    } catch (error) {
      console.error('Error fetching trending hashtags:', error);
      // Fallback to default hashtags on error
      setTrendingHashtags(["#CaniRoma", "#GattiCute", "#AdozioneMilano", "#VeterinarioConsiglio", "#PetFriendly"]);
    } finally {
      setLoadingHashtags(false);
    }
  };
  
  const quickActions = [
    { 
      icon: Camera, 
      label: t('home.createPost'), 
      color: "gradient-primary",
      action: () => navigate('/app/profile')
    },
    { 
      icon: Heart, 
      label: t('common.sos'), 
      color: "bg-destructive hover:bg-destructive/90",
      action: () => navigate('/app/sos')
    },
    { 
      icon: MapPin, 
      label: t('common.events'), 
      color: "bg-secondary hover:bg-secondary/90",
      action: () => navigate('/app/events')
    },
    { 
      icon: Users, 
      label: t('menu.adoptions'), 
      color: "bg-accent hover:bg-accent/90",
      action: () => navigate('/app/adoption')
    },
  ];


  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${feedHeroBg})` }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 pb-20 lg:pb-4">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold gradient-text mb-2 font-space">
          {t('home.welcome').replace('{name}', profile?.display_name || profile?.username || t('home.user'))}
        </h1>
        <p className="text-muted-foreground">{t('home.subtitle')}</p>
      </div>

      {/* Quick Actions */}
      <Card className="glass shadow-card border-white/10 mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Star className="w-5 h-5 mr-2 text-primary" />
            {t('home.quickActions')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={action.action}
                className={`flex flex-col items-center p-6 rounded-xl ${action.color} text-white transition-smooth hover:scale-105 hover:glow-primary`}
              >
                <action.icon className="w-8 h-8 mb-2" />
                <span className="text-sm font-medium">{action.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6 w-full max-w-full overflow-hidden">
          {/* Trending Hashtags */}
          <Card className="glass shadow-card border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-secondary" />
                {t('home.trendingToday')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingHashtags ? (
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-7 w-24 rounded-full" />
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {trendingHashtags.map((hashtag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="px-3 py-1 text-sm rounded-full glass border border-white/20 text-primary hover:text-primary-glow cursor-pointer transition-smooth"
                    >
                      {hashtag}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Personalized Feed with Carousel */}
          <Card className="glass shadow-card border-white/10">
            <CardHeader>
              <CardTitle>{t('home.personalizedFeed')}</CardTitle>
              <CardDescription>{t('home.feedDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={feedType} onValueChange={setFeedType} className="w-full">
                <div className="overflow-x-auto overflow-y-hidden scrollbar-hide -mx-2 px-2">
                  <TabsList className="inline-flex min-w-max gap-2 bg-transparent">
                    <TabsTrigger value="posts" className="flex items-center gap-2 whitespace-nowrap">
                      <Camera className="w-4 h-4" />
                      Post
                    </TabsTrigger>
                    <TabsTrigger value="sos" className="flex items-center gap-2 whitespace-nowrap">
                      <AlertCircle className="w-4 h-4" />
                      SOS
                    </TabsTrigger>
                    <TabsTrigger value="events" className="flex items-center gap-2 whitespace-nowrap">
                      <Calendar className="w-4 h-4" />
                      Eventi
                    </TabsTrigger>
                    <TabsTrigger value="users" className="flex items-center gap-2 whitespace-nowrap">
                      <UserPlus className="w-4 h-4" />
                      Utenti
                    </TabsTrigger>
                    <TabsTrigger value="adoptions" className="flex items-center gap-2 whitespace-nowrap">
                      <Heart className="w-4 h-4" />
                      Adozioni
                    </TabsTrigger>
                  </TabsList>
                </div>

                {loadingFeed ? (
                  <div className="mt-4 space-y-4">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-24 w-full rounded-lg" />
                    ))}
                  </div>
                ) : feedItems.length > 0 ? (
                  <div className="mt-4">
                    <Carousel className="w-full" opts={{ align: "start", loop: false }}>
                      <CarouselContent className="-ml-2 md:-ml-4">
                        {feedItems.map((item, index) => (
                          <CarouselItem key={index} className="pl-2 md:pl-4 basis-full md:basis-1/2">
                            <div 
                              onClick={() => {
                                if (feedType === 'posts') navigate(`/app/post/${item.id}`);
                                else if (feedType === 'sos') navigate('/app/sos');
                                else if (feedType === 'events') navigate('/app/events');
                                else if (feedType === 'users') navigate(`/app/user/${item.username}`);
                                else if (feedType === 'adoptions') navigate('/app/adoption');
                              }}
                              className="p-4 rounded-lg glass border border-white/10 cursor-pointer hover:border-primary/50 transition-all"
                            >
                              <div className="flex items-start space-x-3">
                                <Avatar className="h-10 w-10 flex-shrink-0">
                                  <AvatarImage src={item.profiles?.avatar_url || item.avatar_url} />
                                  <AvatarFallback className="bg-primary text-primary-foreground">
                                    {(item.profiles?.display_name || item.display_name || item.title || '?')[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {item.profiles?.display_name || item.display_name || item.title || item.pet_name || item.username}
                                  </p>
                                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                    {item.content || item.description || item.bio || item.location}
                                  </p>
                                  {feedType === 'posts' && (
                                    <div className="flex items-center space-x-3 mt-2 text-xs text-muted-foreground">
                                      <span className="flex items-center">
                                        <Heart className="w-3 h-3 mr-1" />
                                        {item.likes_count || 0}
                                      </span>
                                      <span className="flex items-center">
                                        <MessageCircle className="w-3 h-3 mr-1" />
                                        {item.comments_count || 0}
                                      </span>
                                    </div>
                                  )}
                                  {feedType === 'events' && item.event_date && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      <Calendar className="w-3 h-3 inline mr-1" />
                                      {new Date(item.event_date).toLocaleDateString('it-IT')}
                                    </p>
                                  )}
                                  {feedType === 'sos' && (
                                    <Badge variant="destructive" className="mt-2 text-xs">
                                      {item.type}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="left-0" />
                      <CarouselNext className="right-0" />
                    </Carousel>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Camera className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">
                      Nessun contenuto disponibile
                    </p>
                  </div>
                )}
              </Tabs>

              <div className="text-center pt-4 border-t border-white/10 w-full">
                <Link to="/app/explore" className="inline-block w-full max-w-xs">
                  <HeroButton className="w-full">
                    <Users className="w-4 h-4 mr-2" />
                    {t('home.exploreCommunity')}
                  </HeroButton>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6 w-full max-w-full overflow-hidden">

          {/* User's Pets */}
          <Card className="glass shadow-card border-white/10">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <PawPrint className="w-5 h-5 mr-2 text-primary" />
                {t('home.yourPets')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center space-x-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  ))}
                </div>
              ) : pets && pets.length > 0 ? (
                <div className="space-y-3">
                  {pets.slice(0, 3).map((pet) => (
                    <div key={pet.id} className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center text-white text-xs font-medium">
                        {pet.avatar_url ? (
                          <img src={pet.avatar_url} alt={pet.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          pet.name[0]
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{pet.name}</p>
                        <p className="text-xs text-muted-foreground">{pet.species}</p>
                      </div>
                    </div>
                  ))}
                  {pets.length > 3 && (
                    <div className="text-center pt-2">
                      <Link to="/app/profile">
                        <SecondaryButton size="sm">
                          {t('home.seeAll').replace('{count}', pets.length.toString())}
                        </SecondaryButton>
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <PawPrint className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('home.noPetsYet')}
                  </p>
                  <Link to="/app/profile">
                    <SecondaryButton size="sm">
                      <Plus className="w-3 h-3 mr-1" />
                      {t('home.addPet')}
                    </SecondaryButton>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* App Info */}
          <Card className="glass shadow-card border-white/10">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center mx-auto mb-2 glow-primary">
                  <Heart className="w-4 h-4 text-white" fill="white" />
                </div>
                <p className="text-xs text-muted-foreground">
                  AstroPet Beta v1.0
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </div>
  );
};

const Index = () => {
  return (
    <NewMainLayout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/pet/:petId" element={<PetProfile />} />
        <Route path="/post/:postId" element={<PostDetail />} />
        <Route path="/followers/:username" element={<FollowersFollowing />} />
        <Route path="/following/:username" element={<FollowersFollowing />} />
        <Route path="/user/:username/followers" element={<FollowersFollowing />} />
        <Route path="/user/:username/following" element={<FollowersFollowing />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/user/:username" element={<UserProfile />} />
        <Route path="/map" element={<Map />} />
        <Route path="/sos" element={<SOS />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/chat/:conversationId" element={<ChatView />} />
        <Route path="/pets" element={<Pets />} />
        <Route path="/adoption" element={<Adoption />} />
        <Route path="/adoption-requests" element={<AdoptionRequests />} />
        <Route path="/my-adoption-requests" element={<MyAdoptionRequests />} />
        <Route path="/events" element={<Events />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/manage/:shopId" element={<ShopManage />} />
        <Route path="/shop/:shopId" element={<ShopView />} />
        <Route path="/connect-shop" element={<ConnectShop />} />
      </Routes>
    </NewMainLayout>
  );
};

export default Index;